import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Search } from '@mui/icons-material';
import { LeadDelivery } from '../../services/pipelineService';
import { fetchDeliveries, getQualityScoreColor } from '../../services/pipelineService';
import DeliveryDialog from './DeliveryDialog';

const DeliveriesTable: React.FC = () => {
  const [deliveries, setDeliveries] = useState<LeadDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    loadDeliveries();
  }, [dateRange]);

  const loadDeliveries = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchDeliveries({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      setDeliveries(data);
    } catch (err) {
      console.error('Error loading deliveries:', err);
      setError('Failed to load deliveries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDelivery = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = async (success: boolean) => {
    setDialogOpen(false);
    if (success) {
      await loadDeliveries();
    }
  };

  const filteredDeliveries = deliveries.filter((delivery) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      delivery.client_name?.toLowerCase().includes(query) ||
      delivery.program_name?.toLowerCase().includes(query)
    );
  });

  const columns: GridColDef[] = [
    {
      field: 'delivery_date',
      headerName: 'Date',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'client_name',
      headerName: 'Client',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'program_name',
      headerName: 'Program',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'lead_count',
      headerName: 'Leads Delivered',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'lead_quality_score',
      headerName: 'Quality Score',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const score = params.value;
        if (!score) return <Typography variant="body2">N/A</Typography>;
        
        const color = getQualityScoreColor(score);
        return (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: `${color}.100`,
              color: `${color}.800`,
              fontWeight: 'medium',
            }}
          >
            {score.toFixed(1)}
          </Box>
        );
      },
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Recorded At',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', height: 600 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">Lead Deliveries</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddDelivery}
        >
          Record Delivery
        </Button>
      </Box>

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by client or program..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Start Date"
          type="date"
          value={dateRange.startDate}
          onChange={(e) =>
            setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
          }
          size="small"
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          label="End Date"
          type="date"
          value={dateRange.endDate}
          onChange={(e) =>
            setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
          }
          size="small"
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 400,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={filteredDeliveries}
          columns={columns}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
            sorting: {
              sortModel: [{ field: 'delivery_date', sort: 'desc' }],
            },
          }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-cell': {
              py: 1,
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        />
      )}

      <DeliveryDialog
        open={dialogOpen}
        onClose={handleDialogClose}
      />
    </Box>
  );
};

export default DeliveriesTable;
